/**
 * GenerateAllDialog — modal del flujo "Generar todo el documento"
 * (Sprint AI-1.g — Sub-fase 5.4).
 *
 * Orquesta los dos agentes (StructureAgent opcional + SectionDraftAgent
 * por sección) para generar el artifact entero con un solo click. El
 * backend persiste cada sección al terminarla (auto-save), así que el
 * UI solo necesita mostrar progreso — al cerrar, el caller invalida
 * queries para refrescar el editor.
 *
 * 4 fases internas:
 *
 *   - 'estimate' → carga estimate + toggles (create_structure,
 *     overwrite_existing) + hint opcional de structure + botón
 *     Generar.
 *   - 'streaming' → progreso en vivo: lista de secciones con check/
 *     spinner/X según status, sección actual destacada, contador
 *     X/N, botón Abortar.
 *   - 'done' → resumen agregado (succeeded/failed/skipped + métricas)
 *     + botones Cerrar / Regenerar.
 *   - 'error' → error legible + Reintentar / Cerrar.
 *
 * Anti-alucinación visible: si un sección falla, se muestra explícito
 * con código + mensaje en el listado, sin esconder el problema. El
 * orchestrator es continue-on-error: una sección fallida NO aborta
 * las siguientes.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  CheckCircle2,
  Layers,
  Loader2,
  Sparkles,
  XCircle,
} from 'lucide-react'

import {
  type DraftAllEstimate,
  estimateDraftAll,
} from '../../api/agents'
import { ApiError } from '../../api/client'
import { useAccessToken } from '../../auth/useAccessToken'
import {
  apiErrorCode,
  useAiVersionLock,
  useAiVersionLockOn,
} from '../../hooks/useAiVersionLock'
import {
  type SectionProgress,
  useStreamAllDraft,
} from '../../hooks/useStreamAllDraft'
import { formatDuration } from '../../lib/formatDuration'
import { parseInstanceKey } from '../projects/comments/commentAnchorText'
import type { TemplateI18nSection } from '../projects/official-viewer/section_types'
import { AiErrorNotice, AiWaitingNotice } from './AiStreamNotices'

interface GenerateAllDialogProps {
  open: boolean
  onClose: () => void
  artifactId: string
  /** Doc 07 — version target. Cuando el artifact tiene canon activo y
   * el user está editando v(N+1) draft, este id apunta a esa draft. Sin
   * este id el backend cae sobre `current_version_id` (caso v1 inicial). */
  versionId?: string | null
  /** Mapa section_id → i18n section, usado para mostrar títulos
   * legibles en el listado de progreso. */
  i18nMap: Map<string, TemplateI18nSection>
  /** True si el template tiene grupos repetibles. Si false, ocultamos
   * el toggle `create_structure` (no haría nada). */
  hasRepeatables: boolean
  /** Callback cuando el run termina exitosamente (done). El caller
   * invalida queries y refresca el editor para mostrar el contenido
   * generado. NO se llama si falla o el user aborta. */
  onCompleted: () => Promise<void> | void
}

type Phase = 'estimate' | 'streaming' | 'done' | 'error'

export function GenerateAllDialog(props: GenerateAllDialogProps) {
  // El contenido se monta al abrir: cada apertura arranca con el estado
  // inicial, y cerrar lo desmonta (lo que corta el stream en curso).
  if (!props.open) return null
  return <GenerateAllContent {...props} />
}

function GenerateAllContent({
  onClose,
  artifactId,
  versionId,
  i18nMap,
  hasRepeatables,
  onCompleted,
}: GenerateAllDialogProps) {
  const { t } = useTranslation('ai')
  const { accessToken } = useAccessToken()
  const reportVersionLock = useAiVersionLock(artifactId, versionId)

  const [createStructure, setCreateStructure] = useState(false)
  const [overwriteExisting, setOverwriteExisting] = useState(false)
  const [structureHint, setStructureHint] = useState('')

  // Estimate fresco en cada apertura (cuenta las secciones ya escritas,
  // que cambian entre aperturas): `gcTime: 0` descarta la cache al
  // desmontarse el modal.
  const estimateQuery = useQuery({
    queryKey: ['draft-all-estimate', artifactId, versionId ?? null],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return estimateDraftAll(accessToken, artifactId, {
        versionId: versionId ?? null,
      })
    },
    enabled: !!accessToken,
    gcTime: 0,
    retry: false,
  })
  // OT-0064 — la estimación ya aplica la regla de editabilidad.
  useAiVersionLockOn(artifactId, versionId, apiErrorCode(estimateQuery.error))
  const estimate: DraftAllEstimate | null = estimateQuery.data ?? null
  const estimateLoading = estimateQuery.isPending
  const estimateError = estimateQuery.error
    ? estimateErrorCode(estimateQuery.error)
    : null

  const stream = useStreamAllDraft(artifactId)

  // Phase derivada del hook + UI local: si el stream está idle Y no
  // hubo error, estamos en 'estimate'.
  const phase: Phase =
    stream.phase === 'streaming'
      ? 'streaming'
      : stream.phase === 'done'
        ? 'done'
        : stream.phase === 'error'
          ? 'error'
          : 'estimate'

  // Cuando termina exitosamente, llamamos al callback del caller para
  // que invalide queries (refresh editor).
  //
  // OT-0064 — el backend corta el run con `error` en cuanto un guardado
  // intermedio encuentra la versión bloqueada (entró a revisión, se aprobó
  // o se descartó). Se aplica al instante (`useAiVersionLock`).
  useEffect(() => {
    if (stream.phase === 'done') {
      void onCompleted()
    } else if (stream.phase === 'error') {
      reportVersionLock(stream.error?.code)
    }
    // onCompleted es stable (caller usa useCallback o función inline ok).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.phase])

  // Esc / backdrop close — bloqueado durante streaming (el run no se
  // puede pausar, solo abortar).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'streaming') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [phase, onClose])

  function handleStart() {
    if (!accessToken) return
    stream.start({
      create_structure: createStructure,
      overwrite_existing: overwriteExisting,
      structure_hint: structureHint.trim() || null,
      versionId: versionId ?? null,
    })
  }

  function handleAbort() {
    stream.abort()
    onClose()
  }

  function handleRegenerate() {
    stream.reset()
  }

  const dialogTitle = (() => {
    if (phase === 'streaming') return t('draft_all.title_streaming')
    if (phase === 'done') return t('draft_all.title_done')
    if (phase === 'error') return t('draft_all.title_error')
    return t('draft_all.title_estimate')
  })()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={dialogTitle}
      onClick={(e) => {
        if (e.target === e.currentTarget && phase !== 'streaming') {
          onClose()
        }
      }}
    >
      <div className="mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-md border border-line bg-surface shadow-lg">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" aria-hidden="true" />
            <h2 className="font-display text-lg text-ink">{dialogTitle}</h2>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {phase === 'estimate' && (
            <EstimateBody
              loading={estimateLoading}
              estimate={estimate}
              estimateError={estimateError}
              hasRepeatables={hasRepeatables}
              createStructure={createStructure}
              onCreateStructureChange={setCreateStructure}
              overwriteExisting={overwriteExisting}
              onOverwriteExistingChange={setOverwriteExisting}
              structureHint={structureHint}
              onStructureHintChange={setStructureHint}
            />
          )}
          {phase === 'streaming' && (
            <StreamingBody
              currentSection={stream.currentSection}
              sections={stream.sections}
              total={stream.totalSectionsToProcess}
              structurePhase={stream.structurePhase}
              waiting={stream.waiting}
              i18nMap={i18nMap}
            />
          )}
          {phase === 'done' && stream.summary && (
            <DoneBody
              summary={stream.summary}
              sections={stream.sections}
              i18nMap={i18nMap}
            />
          )}
          {phase === 'error' && stream.error && (
            <AiErrorNotice
              code={stream.error.code}
              message={stream.error.message}
            />
          )}
        </div>

        {/* Footer */}
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
          {phase === 'estimate' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={
                  estimateLoading ||
                  estimate === null ||
                  estimate.editable_sections_total === 0 ||
                  (estimate.to_generate_max === 0 && !overwriteExisting)
                }
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {t('draft_all.actions.generate')}
              </button>
            </>
          )}
          {phase === 'streaming' && (
            <>
              <button
                type="button"
                onClick={handleAbort}
                className="inline-flex items-center gap-2 rounded-md border border-critical/40 bg-critical-soft/30 px-3 py-1.5 text-sm font-medium text-critical hover:bg-critical-soft/50"
              >
                <XCircle className="h-3.5 w-3.5" />
                {t('draft_all.actions.abort')}
              </button>
              <span className="text-xs text-ink-subtle italic">
                {t('draft_all.streaming_hint')}
              </span>
            </>
          )}
          {phase === 'done' && (
            <>
              <button
                type="button"
                onClick={handleRegenerate}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('draft_all.actions.regenerate')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90"
              >
                {t('dialog.actions.close')}
              </button>
            </>
          )}
          {phase === 'error' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.close')}
              </button>
              <button
                type="button"
                onClick={handleRegenerate}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90"
              >
                {t('dialog.actions.regenerate')}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  )
}

// ─── Sub-bodies ─────────────────────────────────────────────────────────────
function EstimateBody({
  loading,
  estimate,
  estimateError,
  hasRepeatables,
  createStructure,
  onCreateStructureChange,
  overwriteExisting,
  onOverwriteExistingChange,
  structureHint,
  onStructureHintChange,
}: {
  loading: boolean
  estimate: DraftAllEstimate | null
  estimateError: string | null
  hasRepeatables: boolean
  createStructure: boolean
  onCreateStructureChange: (v: boolean) => void
  overwriteExisting: boolean
  onOverwriteExistingChange: (v: boolean) => void
  structureHint: string
  onStructureHintChange: (s: string) => void
}) {
  const { t } = useTranslation('ai')
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-subtle py-6">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('draft_all.loading_estimate')}
      </div>
    )
  }
  if (estimateError) {
    return (
      <div className="rounded-md border border-critical/40 bg-critical-soft/30 p-3 text-sm text-ink">
        <p className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          {estimateError}
        </p>
        <p>{t('draft_all.estimate_error')}</p>
      </div>
    )
  }
  if (!estimate) return null

  // Sin secciones editables: el agente no haría nada.
  if (estimate.editable_sections_total === 0) {
    return (
      <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-sm text-ink-muted">
        {t('draft_all.no_editable_sections_warning')}
      </div>
    )
  }

  // Cuántas secciones se procesarán según el toggle overwrite.
  const willGenerateMin = overwriteExisting
    ? estimate.editable_sections_total
    : estimate.to_generate_min
  const willGenerateMax = overwriteExisting
    ? estimate.editable_sections_total
    : estimate.to_generate_max

  return (
    <div className="space-y-4 text-sm">
      <p className="text-ink-muted font-body leading-relaxed">
        {t('draft_all.intro')}
      </p>

      {/* Toggle: Crear estructura primero */}
      {hasRepeatables && (
        <div className="rounded-md border border-line bg-surface-muted/40 p-3 space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={createStructure}
              onChange={(e) => onCreateStructureChange(e.target.checked)}
              className="mt-0.5 accent-brand"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-brand" />
                <span className="text-sm font-medium text-ink">
                  {t('draft_all.toggle_create_structure_label')}
                </span>
              </div>
              <p className="text-xs text-ink-muted mt-0.5">
                {t('draft_all.toggle_create_structure_hint')}
              </p>
            </div>
          </label>
          {createStructure && (
            <div className="pl-6 pt-2 border-t border-line space-y-1.5">
              <label
                htmlFor="draft-all-structure-hint"
                className="text-[11px] uppercase tracking-wider text-ink-subtle"
              >
                {t('draft_all.structure_hint_label')}
              </label>
              <textarea
                id="draft-all-structure-hint"
                value={structureHint}
                onChange={(e) => onStructureHintChange(e.target.value)}
                placeholder={t('draft_all.structure_hint_placeholder')}
                rows={2}
                maxLength={500}
                className="w-full resize-y rounded-md border border-line bg-canvas px-3 py-2 text-sm font-body leading-relaxed focus:border-brand focus:outline-none"
              />
            </div>
          )}
        </div>
      )}

      {/* Toggle: Overwrite existing */}
      <div className="rounded-md border border-line bg-surface-muted/40 p-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={overwriteExisting}
            onChange={(e) => onOverwriteExistingChange(e.target.checked)}
            className="mt-0.5 accent-brand"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-ink">
              {t('draft_all.toggle_overwrite_label')}
            </span>
            <p className="text-xs text-ink-muted mt-0.5">
              {t('draft_all.toggle_overwrite_hint')}
            </p>
            {overwriteExisting && hasRepeatables && (
              <p className="text-[11px] text-ink-subtle italic mt-2 pt-2 border-t border-line">
                {t('draft_all.anchor_preservation_note')}
              </p>
            )}
          </div>
        </label>
      </div>

      {/* Estadísticas */}
      <div className="rounded-md border border-line bg-canvas p-3 text-xs space-y-1.5">
        <div className="flex items-baseline justify-between">
          <span className="text-ink-subtle">
            {t('draft_all.stats.editable_total')}
          </span>
          <span className="font-medium text-ink">
            {estimate.editable_sections_total}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-ink-subtle">
            {t('draft_all.stats.already_written')}
          </span>
          <span className="font-medium text-ink">
            {estimate.already_written}
          </span>
        </div>
        <div className="flex items-baseline justify-between border-t border-line pt-1.5">
          <span className="text-ink-subtle">
            {t('draft_all.stats.will_generate')}
          </span>
          <span className="font-medium text-brand">
            {willGenerateMin === willGenerateMax
              ? willGenerateMax
              : `${willGenerateMin}–${willGenerateMax}`}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-ink-subtle">
            {t('draft_all.stats.estimated_tokens')}
          </span>
          <span className="font-mono text-ink">
            ~
            {formatTokenRange(
              estimate.estimated_total_tokens_min,
              estimate.estimated_total_tokens_max,
            )}
          </span>
        </div>
      </div>

      {/* Notas del backend */}
      {estimate.notes.length > 0 && (
        <ul className="rounded-md border border-line bg-surface-muted/40 p-2 text-xs text-ink-muted space-y-0.5">
          {estimate.notes.map((n) => (
            <li key={n}>· {n}</li>
          ))}
        </ul>
      )}

      <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-xs text-ink-muted">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-review shrink-0 mt-0.5" />
          <p>{t('draft_all.warning_long_running')}</p>
        </div>
      </div>
    </div>
  )
}

function StreamingBody({
  currentSection,
  sections,
  total,
  structurePhase,
  waiting,
  i18nMap,
}: {
  currentSection: ReturnType<typeof useStreamAllDraft>['currentSection']
  sections: Map<string, SectionProgress>
  total: number
  structurePhase: ReturnType<typeof useStreamAllDraft>['structurePhase']
  waiting: ReturnType<typeof useStreamAllDraft>['waiting']
  i18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('ai')
  const sectionsList = Array.from(sections.values())
  const doneCount = sectionsList.filter((s) => s.status === 'done').length
  const failedCount = sectionsList.filter((s) => s.status === 'error').length
  const skippedCount = sectionsList.filter(
    (s) => s.status === 'skipped',
  ).length

  return (
    <div className="space-y-4">
      {/* Structure phase status (si aplica) */}
      {structurePhase && (
        <div
          className={`rounded-md border p-3 text-sm ${
            structurePhase.status === 'failed'
              ? 'border-critical/40 bg-critical-soft/30'
              : 'border-brand/30 bg-brand-soft/20'
          }`}
        >
          <div className="flex items-center gap-2">
            {structurePhase.status === 'running' && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />
            )}
            {structurePhase.status === 'done' && (
              <CheckCircle2 className="h-3.5 w-3.5 text-official" />
            )}
            {structurePhase.status === 'failed' && (
              <XCircle className="h-3.5 w-3.5 text-critical" />
            )}
            <span className="font-medium text-ink">
              {t(`draft_all.structure_phase.${structurePhase.status}`)}
            </span>
          </div>
          {structurePhase.status === 'done' && structurePhase.rationale && (
            <p className="mt-1 text-xs text-ink-muted leading-relaxed pl-5">
              {structurePhase.rationale}
            </p>
          )}
          {structurePhase.status === 'failed' && structurePhase.errorMessage && (
            <p className="mt-1 text-xs text-critical font-mono pl-5">
              {structurePhase.errorMessage}
            </p>
          )}
        </div>
      )}

      {/* Progreso global */}
      <div className="rounded-md border border-line bg-surface-muted/40 p-3">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">
            {t('draft_all.progress_label')}
          </span>
          <span className="font-mono text-sm text-ink">
            {currentSection
              ? t('draft_all.progress_counter', {
                  current: currentSection.index + 1,
                  total: currentSection.total,
                })
              : t('draft_all.progress_counter', {
                  current: doneCount + failedCount,
                  total,
                })}
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-line overflow-hidden">
          <div
            className="h-full bg-brand transition-all duration-300"
            style={{
              // `total` viene del `topology_built` event = secciones a
              // procesar (post-filter, excluye pre-skipped). Por eso
              // NO sumamos `skippedCount` aquí — esas se contaron en el
              // filter previo y ya están fuera del divisor. Sumar
              // skippedCount producía progress >100% (4 pre-skipped /
              // 2 to_process = 200%) que el clamp ocultaba mostrando
              // barra completa desde el inicio.
              width: `${
                total > 0
                  ? Math.min(
                      100,
                      ((doneCount + failedCount) / total) * 100,
                    )
                  : 0
              }%`,
            }}
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ink-subtle">
          <span className="inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-official" />
            {t('draft_all.stats.done', { count: doneCount })}
          </span>
          {failedCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <XCircle className="h-3 w-3 text-critical" />
              {t('draft_all.stats.failed', { count: failedCount })}
            </span>
          )}
          {skippedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-ink-subtle">
              {t('draft_all.stats.skipped', { count: skippedCount })}
            </span>
          )}
        </div>
      </div>

      <AiWaitingNotice waiting={waiting} />

      {/* Listado de secciones */}
      <SectionListing sections={sectionsList} i18nMap={i18nMap} />
    </div>
  )
}

function DoneBody({
  summary,
  sections,
  i18nMap,
}: {
  summary: NonNullable<ReturnType<typeof useStreamAllDraft>['summary']>
  sections: Map<string, SectionProgress>
  i18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('ai')
  const sectionsList = Array.from(sections.values())

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-official/30 bg-official-soft/20 p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-4 w-4 text-official" />
          <h3 className="font-display text-base text-ink">
            {t('draft_all.done.title')}
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-ink-subtle">
              {t('draft_all.stats.succeeded_label')}
            </span>{' '}
            <span className="font-mono text-ink">{summary.succeeded}</span>
          </div>
          {summary.failed > 0 && (
            <div>
              <span className="text-ink-subtle">
                {t('draft_all.stats.failed_label')}
              </span>{' '}
              <span className="font-mono text-critical">
                {summary.failed}
              </span>
            </div>
          )}
          {summary.skippedExisting > 0 && (
            <div>
              <span className="text-ink-subtle">
                {t('draft_all.stats.skipped_existing_label')}
              </span>{' '}
              <span className="font-mono text-ink">
                {summary.skippedExisting}
              </span>
            </div>
          )}
          <div>
            <span className="text-ink-subtle">
              {t('draft_all.stats.total_tokens_label')}
            </span>{' '}
            <span className="font-mono text-ink">
              {formatTokens(summary.aggregateTotalTokens)}
            </span>
          </div>
          <div className="col-span-2">
            <span className="text-ink-subtle">
              {t('draft_all.stats.latency_label')}
            </span>{' '}
            <span className="font-mono text-ink">
              {formatDuration(summary.aggregateLatencyMs, t)}
            </span>
          </div>
        </div>
      </div>

      {/* Listado final con todos los resultados */}
      <SectionListing sections={sectionsList} i18nMap={i18nMap} />
    </div>
  )
}

function SectionListing({
  sections,
  i18nMap,
}: {
  sections: SectionProgress[]
  i18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('ai')
  if (sections.length === 0) {
    return (
      <p className="text-xs text-ink-subtle italic">
        {t('draft_all.empty_listing')}
      </p>
    )
  }
  return (
    <ul className="rounded-md border border-line divide-y divide-line">
      {sections.map((s) => {
        const title = i18nMap.get(s.sectionId)?.title ?? s.sectionId
        const breadcrumb = buildInstanceBreadcrumb(s.instanceKey, i18nMap)
        return (
          <li
            key={`${s.sectionId}::${s.instanceKey}`}
            className="flex items-center gap-2 px-3 py-2 text-sm"
          >
            <StatusIcon status={s.status} />
            <div className="flex-1 min-w-0">
              <div className="text-ink truncate">{title}</div>
              {breadcrumb && (
                <div className="text-[11px] text-ink-subtle truncate mt-0.5">
                  {breadcrumb}
                </div>
              )}
            </div>
            {s.status === 'done' && s.draftChars !== undefined && (
              <span className="font-mono text-[10px] text-ink-subtle shrink-0">
                {s.draftChars} chars
                {s.tokensInput !== undefined && s.tokensOutput !== undefined
                  ? ` · ${formatTokens(s.tokensInput + s.tokensOutput)} tok`
                  : ''}
              </span>
            )}
            {s.status === 'error' && (
              <span
                className="font-mono text-[10px] text-critical shrink-0 truncate max-w-xs"
                title={s.errorMessage ?? s.errorCode ?? 'error'}
              >
                {s.errorCode ?? 'error'}
              </span>
            )}
            {s.status === 'skipped' && s.skipReason && (
              <span className="text-[11px] text-ink-subtle italic shrink-0">
                {t(`draft_all.skip_reason.${s.skipReason}`, {
                  defaultValue: s.skipReason,
                })}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Convierte un `instance_key` (ej. `casos_de_uso[0].flujos_alternos[0].descripcion_flujo_alterno`)
 * en un breadcrumb legible por humanos (ej. "Casos de uso 1 › Flujos alternos 1").
 * El último segmento (la hoja) se omite porque ya se muestra como title
 * en la línea superior. Top-level sections (sin segmentos indexados)
 * retornan null. */
function buildInstanceBreadcrumb(
  instanceKey: string,
  i18nMap: Map<string, TemplateI18nSection>,
): string | null {
  const parts = parseInstanceKey(instanceKey).flatMap((seg) =>
    seg.index === undefined
      ? []
      : [`${i18nMap.get(seg.section_id)?.title ?? seg.section_id} ${seg.index + 1}`],
  )
  return parts.length > 0 ? parts.join(' › ') : null
}

function StatusIcon({ status }: { status: SectionProgress['status'] }) {
  if (status === 'running') {
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-brand shrink-0" />
  }
  if (status === 'done') {
    return <CheckCircle2 className="h-3.5 w-3.5 text-official shrink-0" />
  }
  if (status === 'error') {
    return <XCircle className="h-3.5 w-3.5 text-critical shrink-0" />
  }
  if (status === 'skipped') {
    return (
      <span
        className="h-3.5 w-3.5 rounded-full border border-line shrink-0"
        aria-hidden="true"
      />
    )
  }
  return (
    <span
      className="h-3.5 w-3.5 rounded-full border border-line shrink-0"
      aria-hidden="true"
    />
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function estimateErrorCode(err: Error): string {
  return err instanceof ApiError ? err.code : err.message
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return String(n)
}

function formatTokenRange(min: number, max: number): string {
  if (min === max) return formatTokens(max)
  return `${formatTokens(min)}–${formatTokens(max)}`
}
